import { useState, useEffect, ReactNode, createContext, useContext } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const ADMIN_GROUP = 'WholesaleAdmins';

interface AdminContextType {
  isAdmin: boolean;
  isLoading: boolean;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  isLoading: true,
});

/**
 * Hook to check if current user is an admin
 */
export function useIsAdmin() {
  return useContext(AdminContext);
}

interface RequireAdminProps {
  children: ReactNode;
}

/**
 * RequireAdmin Wrapper Component
 *
 * This component checks if the authenticated user is a member of the WholesaleAdmins
 * Cognito group by inspecting the JWT token's cognito:groups claim.
 * - If admin: renders children
 * - If not admin: redirects to home page
 * - If loading: shows loading spinner
 */
export default function RequireAdmin({ children }: RequireAdminProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        setIsAdmin(false);
        return;
      }

      // Get the payload from the ID token
      const payload = idToken.payload;

      // Check cognito:groups claim
      const groups = payload['cognito:groups'];

      if (Array.isArray(groups)) {
        setIsAdmin(groups.includes(ADMIN_GROUP));
      } else if (typeof groups === 'string') {
        setIsAdmin(groups === ADMIN_GROUP);
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify admin status');
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-block">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-12 h-12 border-4 border-leather border-t-brass rounded-full"
            />
          </div>
          <p className="mt-4 text-text-muted uppercase tracking-wide text-sm">
            Verifying admin access...
          </p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-display text-leather mb-2">
            Unable to Verify Access
          </h2>
          <p className="text-text-muted mb-6">{error}</p>
          <button onClick={checkAdminStatus} className="btn-primary">
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  // Not admin - redirect to home
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Admin - render children with context
  return (
    <AdminContext.Provider value={{ isAdmin, isLoading }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    </AdminContext.Provider>
  );
}

/**
 * Hook to check admin status without requiring the component wrapper
 * Useful for conditional rendering in other components
 */
export function useAdminStatus() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        if (!idToken) {
          setIsAdmin(false);
          return;
        }

        const payload = idToken.payload;
        const groups = payload['cognito:groups'];

        if (Array.isArray(groups)) {
          setIsAdmin(groups.includes(ADMIN_GROUP));
        } else if (typeof groups === 'string') {
          setIsAdmin(groups === ADMIN_GROUP);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, []);

  return { isAdmin, isLoading };
}
